import { useContext, useEffect, useRef } from 'react'

import { useAccount, useMsal } from '@azure/msal-react'
import axios from 'axios'

import { getAuthToken } from './AccessTokenService'
import { msalService, userInfo } from '../AuthConfig'
import { NetworkContext } from '@/context/providers/NetworkContext'
import { ErrorConstants } from '@/utils/constants/ErrorConstants'

const NetworkInterceptor = ({ children }: { children: React.JSX.Element }) => {
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0])
  const { setIsOnline } = useContext(NetworkContext)

  msalService.instance = instance
  msalService.account = account
  userInfo.userName = account?.name
  userInfo.userMailId = account?.username

  const offlineCheckIntervalRef = useRef(null)

  const updateOnlineStatus = (): void => {
    setIsOnline(true)
    if (offlineCheckIntervalRef.current) {
      clearInterval(offlineCheckIntervalRef.current)
      offlineCheckIntervalRef.current = null
    }
  }

  const updateOfflineStatus = (): void => {
    setIsOnline(false)
    if (!offlineCheckIntervalRef.current) {
      offlineCheckIntervalRef.current = setInterval(() => {
        if (navigator.onLine) {
          updateOnlineStatus()
        }
      }, 1000)
    }
  }

  useEffect(() => {
    const handleOnline = () => updateOnlineStatus()
    const handleOffline = () => updateOfflineStatus()

    if (navigator.onLine) {
      updateOnlineStatus()
    } else {
      updateOfflineStatus()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    const axiosRequestInterceptor = async (request: any) => {
      if (!account) {
        throw Error('No active account! Verify a user has been signed in.')
      }
      const accessToken = await getAuthToken(account, instance)
      request.headers.Authorization = `Bearer ${accessToken}`
      return request
    }

    const axiosResponseInterceptor = (response: any) => response

    const axiosErrorInterceptor = (error: any) => {
      if (error.name === ErrorConstants.abortError || error.name === ErrorConstants.canceledError) {
        console.log('Request aborted, not offline')
      } else if (!error.response) {
        updateOfflineStatus()
      }
      return returnAxiosErrorType(error)
    }

    const returnAxiosErrorType = (error: any) => {
      const errorResponse = error.response
      const errorDetails = errorResponse?.data?.Details?.[0] || ''
      if (errorResponse?.status === 403) {
        return Promise.reject(new Error(ErrorConstants.noEditorAccessError))
      } else if (errorResponse?.status === 400 && (errorDetails === ErrorConstants.imageSizeError || errorDetails === ErrorConstants.requestBodyError)) {
        return Promise.reject(new Error(ErrorConstants.imageSizeError))
      } else if (errorResponse?.status === 400 && errorDetails === ErrorConstants.imageCorruptError) {
        return Promise.reject(new Error(ErrorConstants.imageCorruptError))
      }
      return Promise.reject(new Error(error.name))
    }

    const requestInterceptorId = axios.interceptors.request.use(axiosRequestInterceptor)
    const responseInterceptorId = axios.interceptors.response.use(
      axiosResponseInterceptor,
      axiosErrorInterceptor,
    )

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (offlineCheckIntervalRef.current) {
        clearInterval(offlineCheckIntervalRef.current)
      }
      axios.interceptors.request.eject(requestInterceptorId)
      axios.interceptors.response.eject(responseInterceptorId)
    }
  }, [account, instance, setIsOnline])

  return <>{children}</>
}

export default NetworkInterceptor
